import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config';
import UserDetailModal from '../components/UserDetailModal';

/**
 * Admin dashboard. Visible only to users with role='admin'.
 * Tabs:
 *   - Overview: aggregate stats
 *   - Users: list, search, promote/suspend
 *   - AI Queries: every Q&A with full text on click
 *   - API Usage: per-call log
 *   - Payments: Stripe events ledger
 */
function AdminDashboard({ onBack, onSignOut }) {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [aiQueries, setAiQueries] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [apiUsage, setApiUsage] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsDashboard, setPaymentsDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedQuery, setExpandedQuery] = useState(null);

  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [aiUserFilter, setAiUserFilter] = useState({ id: '', email: '' }); // when drilled-down from users tab
  const [aiKeywordFilter, setAiKeywordFilter] = useState('');
  const [aiErrorsOnly, setAiErrorsOnly] = useState(false);
  const [paymentsUserFilter, setPaymentsUserFilter] = useState({ id: '', email: '' });

  // User detail modal state
  const [detailUserId, setDetailUserId] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('astrogem_token') : null;

  const authedFetch = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token },
    }).then(r => r.json());

  useEffect(() => {
    if (!token) { setError('Not signed in.'); setLoading(false); return; }
    refreshAll();
  }, [token]); // eslint-disable-line

  // Re-fetch users when the search/filter changes (debounced)
  useEffect(() => {
    if (!token) return;
    const handle = setTimeout(() => {
      const qs = new URLSearchParams({ limit: '200' });
      if (userSearch) qs.set('q', userSearch);
      if (userRoleFilter) qs.set('role', userRoleFilter);
      authedFetch('/api/v3/admin/users?' + qs.toString())
        .then(u => { if (u.success) setUsers(u.users || []); });
    }, 250);
    return () => clearTimeout(handle);
  }, [userSearch, userRoleFilter, token]); // eslint-disable-line

  // Re-fetch AI queries when filters change
  useEffect(() => {
    if (!token) return;
    const handle = setTimeout(() => {
      const qs = new URLSearchParams({ limit: '50' });
      if (aiUserFilter.id) qs.set('user_id', aiUserFilter.id);
      if (aiKeywordFilter) qs.set('q', aiKeywordFilter);
      if (aiErrorsOnly) qs.set('errors_only', '1');
      authedFetch('/api/v3/admin/ai-queries?' + qs.toString())
        .then(a => {
          if (a.success) {
            setAiQueries(a.queries || []);
            setAiSummary(a.summary || null);
          }
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [aiUserFilter.id, aiKeywordFilter, aiErrorsOnly, token]); // eslint-disable-line

  // Re-fetch payments when user filter changes
  useEffect(() => {
    if (!token) return;
    const qs = new URLSearchParams({ limit: '100' });
    if (paymentsUserFilter.id) qs.set('user_id', paymentsUserFilter.id);
    authedFetch('/api/v3/admin/payments?' + qs.toString())
      .then(p => { if (p.success) setPayments(p.payments || []); });
  }, [paymentsUserFilter.id, token]); // eslint-disable-line

  const refreshAll = () => {
    setLoading(true);
    Promise.all([
      authedFetch('/api/v3/admin/stats'),
      authedFetch('/api/v3/admin/users?limit=200'),
      authedFetch('/api/v3/admin/ai-queries?limit=50'),
      authedFetch('/api/v3/admin/api-usage?limit=50'),
      authedFetch('/api/v3/admin/payments?limit=100'),
      authedFetch('/api/v3/admin/payments-dashboard'),
    ])
      .then(([s, u, a, ap, p, pd]) => {
        if (!s.success) throw new Error(s.error || 'Failed to load stats');
        setStats(s);
        setUsers(u.users || []);
        setAiQueries(a.queries || []);
        setAiSummary(a.summary || null);
        setApiUsage(ap.usage || []);
        setPayments(p.payments || []);
        setPaymentsDashboard(pd.success ? pd : null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  const viewUserAi = (user) => {
    setAiUserFilter({ id: user.id, email: user.email });
    setTab('ai');
  };
  const viewUserPayments = (user) => {
    setPaymentsUserFilter({ id: user.id, email: user.email });
    setTab('payments');
  };
  const clearAiFilter = () => setAiUserFilter({ id: '', email: '' });
  const clearPaymentsFilter = () => setPaymentsUserFilter({ id: '', email: '' });

  // After any user edit in the modal, refresh the list so columns reflect changes
  const reloadUsers = () => {
    const qs = new URLSearchParams({ limit: '200' });
    if (userSearch) qs.set('q', userSearch);
    if (userRoleFilter) qs.set('role', userRoleFilter);
    authedFetch('/api/v3/admin/users?' + qs.toString())
      .then(u => { if (u.success) setUsers(u.users || []); });
  };

  // Export current users list as CSV (respects active search + role filter)
  const exportUsersCsv = () => {
    if (!users.length) return alert('No users to export.');
    const cols = [
      'id', 'email', 'name', 'role', 'status',
      'ai_credits_balance', 'ai_count_30d', 'ai_cost_30d', 'api_count_30d',
      'lifetime_revenue_cents',
      'subscription_status', 'subscription_period_end',
      'stripe_customer_id', 'stripe_subscription_id',
      'created_at', 'last_login_at',
    ];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      // Quote and escape if contains comma, quote, or newline
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const formatVal = (col, v) => {
      if (v == null) return '';
      if (col === 'created_at' || col === 'last_login_at' || col === 'subscription_period_end') {
        return new Date(v).toISOString();
      }
      if (col === 'ai_cost_30d') return Number(v).toFixed(4);
      if (col === 'lifetime_revenue_cents') return (Number(v) / 100).toFixed(2);
      return v;
    };
    const lines = [
      cols.join(','),
      ...users.map(u => cols.map(c => escape(formatVal(c, u[c]))).join(',')),
    ];
    const csv = lines.join('\n');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `astrogem-users-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const updateUserRole = async (userId, newRole) => {
    if (!window.confirm(`Change this user's role to "${newRole}"?`)) return;
    const r = await authedFetch(`/api/v3/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    if (r.success) {
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } else {
      alert('Failed: ' + (r.error || 'Unknown'));
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    if (!window.confirm(`Set this user's status to "${newStatus}"?`)) return;
    const r = await authedFetch(`/api/v3/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r.success) {
      setUsers(users.map(u => u.id === userId ? { ...u, status: newStatus } : u));
    } else {
      alert('Failed: ' + (r.error || 'Unknown'));
    }
  };

  const grantCredits = async (user) => {
    const amountStr = window.prompt(`Grant AI credits to ${user.email}\n(complimentary — no charge to user)\n\nAmount (positive integer):`);
    if (!amountStr) return;
    const amount = parseInt(amountStr, 10);
    if (!amount || amount < 1) return alert('Invalid amount');
    const note = window.prompt('Optional note (e.g. "Family member", "Beta tester"):', '') || '';
    const r = await authedFetch(`/api/v3/admin/users/${user.id}/grant-credits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, note }),
    });
    if (r.success) {
      alert(`✓ Granted ${amount} credits. New balance: ${r.new_balance}`);
      setUsers(users.map(u => u.id === user.id ? { ...u, ai_credits_balance: r.new_balance } : u));
    } else {
      alert('Failed: ' + (r.error || 'Unknown'));
    }
  };

  const viewFullQuery = async (id) => {
    if (expandedQuery?.id === id) { setExpandedQuery(null); return; }
    const r = await authedFetch(`/api/v3/admin/ai-queries/${id}`);
    if (r.success) setExpandedQuery(r.query);
  };

  if (loading) return <Centered>Loading admin dashboard…</Centered>;
  if (error) return <Centered>⚠️ {error} <br /><button onClick={onBack} className="mt-3 underline">Back</button></Centered>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white">
      <nav className="border-b border-red-900/50 bg-red-950/30 backdrop-blur sticky top-0 z-50">
        <div className="px-4 py-3 max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-white/60 hover:text-white text-sm">← Back to app</button>
            <span className="text-red-300 font-bold text-sm">🔐 ADMIN PANEL</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refreshAll} className="text-xs text-white/50 hover:text-white">🔄 Refresh</button>
            <button onClick={onSignOut} className="text-xs text-white/50 hover:text-white">Sign out</button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-2 mb-6 border-b border-slate-800 overflow-x-auto">
          {[
            ['overview', '📊 Overview'],
            ['users', `👥 Users (${stats?.users?.total || 0})`],
            ['ai', `🪔 AI Queries (${stats?.ai?.month_queries || 0})`],
            ['api', '⚡ API Usage'],
            ['payments', `💳 Payments (${stats?.revenue?.month_payments || 0})`],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm whitespace-nowrap transition ${tab === id ? 'border-b-2 border-purple-400 text-white' : 'text-white/50 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={stats.users.total} sub={`+${stats.users.new_30d} in 30d`} />
              <StatCard title="DAU / MAU" value={`${stats.users.dau} / ${stats.users.mau}`} sub="signed-in users active" />
              <StatCard title="AI Cost (30d)" value={`$${stats.ai.month_cost_usd?.toFixed(2)}`} sub={`${stats.ai.month_queries} queries`} />
              <StatCard title="Revenue (30d)" value={`$${stats.revenue.month_dollars}`} sub={`${stats.revenue.month_payments} payments`} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card title="Users by Role">
                <ul className="text-sm">
                  {Object.entries(stats.users.by_role).map(([role, n]) => (
                    <li key={role} className="flex justify-between py-1.5 border-b border-slate-800">
                      <span className="capitalize">{role}</span>
                      <strong>{n}</strong>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card title="Anonymous Trial Usage">
                <div className="text-3xl font-bold">{stats.anonymous_usage.total_queries}</div>
                <p className="text-white/60 text-sm">free trial AI questions across {stats.anonymous_usage.unique_ips} unique IPs</p>
                <p className="text-white/40 text-xs mt-2">Each IP gets 3 free queries before signup is required.</p>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card title="🪔 Top users by AI cost (30d)">
                {stats.top_users.by_ai_cost_30d.length === 0 ? (
                  <p className="text-white/40 text-sm">No signed-in AI queries yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                      <tr><th className="py-2">Email</th><th>Role</th><th className="text-right">Queries</th><th className="text-right">Cost</th></tr>
                    </thead>
                    <tbody>
                      {stats.top_users.by_ai_cost_30d.map(u => (
                        <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-900/50 cursor-pointer"
                            onClick={() => setDetailUserId(u.id)}>
                          <td className="py-2">{u.email}</td>
                          <td><RoleBadge role={u.role} /></td>
                          <td className="text-right">{u.queries}</td>
                          <td className="text-right">${u.cost_usd?.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <Card title="👥 Power users (most chart subjects)">
                {(stats.top_users.by_profile_count || []).length === 0 ? (
                  <p className="text-white/40 text-sm">No saved profiles yet. Once users start charting people, their counts appear here.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                      <tr><th className="py-2">Email</th><th>Role</th><th className="text-right">Charts</th></tr>
                    </thead>
                    <tbody>
                      {(stats.top_users.by_profile_count || []).map(u => (
                        <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-900/50 cursor-pointer"
                            onClick={() => setDetailUserId(u.id)}>
                          <td className="py-2">{u.email}</td>
                          <td><RoleBadge role={u.role} /></td>
                          <td className="text-right text-amber-300 font-bold">{u.profile_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && (
          <Card title={`All Users (${users.length} shown)`}>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <input
                type="text"
                placeholder="🔍 Search by email or name…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                className="flex-1 min-w-[240px] bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-purple-400"
              />
              <div className="flex gap-1 flex-wrap">
                {['', 'free', 'pro', 'developer', 'scale', 'comp', 'admin'].map(r => (
                  <button
                    key={r || 'all'}
                    onClick={() => setUserRoleFilter(r)}
                    className={`text-xs px-2 py-1 rounded transition ${userRoleFilter === r ? 'bg-purple-600 text-white' : 'bg-slate-800 text-white/60 hover:text-white'}`}
                  >
                    {r || 'All'}
                  </button>
                ))}
              </div>
              <button
                onClick={exportUsersCsv}
                className="text-xs px-3 py-1.5 bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/40 rounded font-bold transition"
                title="Download current filtered list as CSV"
              >
                ⬇️ Export CSV
              </button>
            </div>
            <p className="text-xs text-white/40 mb-3">💡 Tip: click any row to open the full user detail panel</p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                  <tr>
                    <th className="py-2">Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="text-right">🪙 Credits</th>
                    <th className="text-right">👥 Charts</th>
                    <th className="text-right">AI 30d</th>
                    <th className="text-right">AI Cost 30d</th>
                    <th className="text-right">API 30d</th>
                    <th className="text-right">Lifetime $</th>
                    <th>Sub</th>
                    <th>Created</th>
                    <th>Last Login</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => {
                    // Stop click on interactive cells from bubbling to the row click handler
                    const stop = (e) => e.stopPropagation();
                    return (
                    <tr
                      key={u.id}
                      onClick={() => setDetailUserId(u.id)}
                      className="border-b border-slate-800 hover:bg-slate-900/50 cursor-pointer"
                    >
                      <td className="py-2">
                        <div className="font-medium hover:text-purple-300">{u.email}</div>
                        {u.name && <div className="text-xs text-white/40">{u.name}</div>}
                      </td>
                      <td onClick={stop}>
                        <select
                          value={u.role}
                          onChange={(e) => updateUserRole(u.id, e.target.value)}
                          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"
                        >
                          {['free','pro','developer','scale','enterprise','comp','admin'].map(r => <option key={r}>{r}</option>)}
                        </select>
                      </td>
                      <td onClick={stop}>
                        <select
                          value={u.status}
                          onChange={(e) => updateUserStatus(u.id, e.target.value)}
                          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"
                        >
                          {['active','suspended'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="text-right text-xs">
                        {u.profile_count > 0 ? (
                          <span className={u.profile_count >= 5 ? 'text-amber-300 font-bold' : 'text-white'}>
                            {u.profile_count}
                          </span>
                        ) : <span className="text-white/30">0</span>}
                      </td>
                      <td className="text-right text-xs">{(u.ai_credits_balance || 0).toLocaleString()}</td>
                      <td className="text-right text-xs">{u.ai_count_30d || 0}</td>
                      <td className="text-right text-xs">${(u.ai_cost_30d || 0).toFixed(4)}</td>
                      <td className="text-right text-xs">{(u.api_count_30d || 0).toLocaleString()}</td>
                      <td className="text-right text-xs text-green-300">${((u.lifetime_revenue_cents || 0) / 100).toFixed(2)}</td>
                      <td className="text-xs text-white/60">{u.subscription_status || '—'}</td>
                      <td className="text-xs text-white/60">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="text-xs text-white/60">
                        {u.last_login_at
                          ? <span title={new Date(u.last_login_at).toLocaleString()}>{relativeDate(u.last_login_at)}</span>
                          : <span className="text-white/30">never</span>}
                      </td>
                      <td className="text-right whitespace-nowrap text-xs" onClick={stop}>
                        <button onClick={(e) => { e.stopPropagation(); viewUserAi(u); }} className="text-purple-400 hover:underline mr-2" title="See this user's AI questions">
                          🪔 AI
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); viewUserPayments(u); }} className="text-green-400 hover:underline mr-2" title="See this user's payments">
                          💳 $
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDetailUserId(u.id); }} className="text-purple-300 hover:underline" title="Open full user detail panel">
                          ⚙️ Manage
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <p className="text-white/40 text-sm text-center py-8">No users match the current filter.</p>
            )}
          </Card>
        )}

        {/* AI QUERIES */}
        {tab === 'ai' && (
          <Card title={`AI Queries — ${aiSummary?.total || 0} match, $${(aiSummary?.total_cost || 0).toFixed(4)} cost, ${(aiSummary?.total_tokens || 0).toLocaleString()} tokens`}>
            {/* Filter bar */}
            <div className="flex flex-wrap gap-2 items-center mb-4">
              {aiUserFilter.email && (
                <span className="inline-flex items-center gap-1 bg-purple-600/30 border border-purple-500 rounded px-2 py-1 text-xs">
                  user: {aiUserFilter.email}
                  <button onClick={clearAiFilter} className="ml-1 text-white/60 hover:text-white">×</button>
                </span>
              )}
              <input
                type="text"
                placeholder="🔍 Search question / response…"
                value={aiKeywordFilter}
                onChange={e => setAiKeywordFilter(e.target.value)}
                className="flex-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-purple-400"
              />
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={aiErrorsOnly} onChange={e => setAiErrorsOnly(e.target.checked)} />
                <span>Errors only</span>
              </label>
            </div>

            {aiQueries.length === 0 ? <p className="text-white/40 text-sm text-center py-8">No queries match the current filter.</p> : (
              <div className="space-y-2">
                {aiQueries.map(q => (
                  <div key={q.id} className="bg-slate-900 border border-slate-800 rounded p-3">
                    <div className="flex items-center justify-between text-xs text-white/40 mb-1 flex-wrap gap-2">
                      <span>{new Date(q.created_at).toLocaleString()} · IP {q.ip}</span>
                      <span className="flex items-center gap-2">
                        {q.user_id ? (
                          <button
                            onClick={() => setDetailUserId(q.user_id)}
                            className="text-purple-300 hover:text-purple-200 hover:underline text-xs"
                            title="Open user detail"
                          >
                            {q.user_name ? <strong className="text-white">{q.user_name}</strong> : null}
                            {q.user_name && q.user_email ? ' · ' : ''}
                            <span>{q.user_email || q.user_id.slice(0, 12) + '…'}</span>
                            {q.user_role && <span className="ml-1 text-[10px] uppercase text-white/40">({q.user_role})</span>}
                          </button>
                        ) : (
                          <span className="text-white/40 italic">(anonymous)</span>
                        )}
                        <span className="text-white/30">·</span>
                        <span>{q.total_tokens || 0} tok · ${(q.cost_usd || 0).toFixed(5)} · {q.duration_ms}ms</span>
                      </span>
                    </div>
                    <p className="text-purple-300 text-sm font-semibold">Q: {q.question_preview}</p>
                    <p className="text-white/80 text-sm mt-1 whitespace-pre-line">
                      {expandedQuery?.id === q.id ? expandedQuery.response : q.response_preview + (q.response_preview?.length === 300 ? '…' : '')}
                    </p>
                    {q.error && <p className="text-red-300 text-xs mt-1">⚠ {q.error}</p>}
                    <button onClick={() => viewFullQuery(q.id)} className="text-xs text-purple-400 hover:underline mt-1">
                      {expandedQuery?.id === q.id ? 'Collapse' : 'View full Q&A'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* API USAGE */}
        {tab === 'api' && (
          <Card title="Recent API Calls">
            {apiUsage.length === 0 ? <p className="text-white/40 text-sm">No API calls yet from signed-in users.</p> : (
              <table className="w-full text-sm">
                <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                  <tr><th className="py-2">When</th><th>User</th><th>Endpoint</th><th>Status</th><th className="text-right">ms</th></tr>
                </thead>
                <tbody>
                  {apiUsage.map(c => (
                    <tr key={c.id} className="border-b border-slate-800">
                      <td className="py-2 text-xs text-white/60">{new Date(c.created_at).toLocaleString()}</td>
                      <td className="text-xs">{c.user_id?.slice(0,12) || '—'}…</td>
                      <td className="font-mono text-xs">{c.method} {c.endpoint}</td>
                      <td className="text-xs"><StatusPill code={c.status_code} /></td>
                      <td className="text-right text-xs">{c.response_time_ms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}

        {/* PAYMENTS */}
        {tab === 'payments' && (
          <div className="space-y-6">
            {/* Stat cards */}
            {paymentsDashboard && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard title="MRR" value={`$${paymentsDashboard.mrr_dollars}`} sub={`${paymentsDashboard.active_subscribers_total} active sub${paymentsDashboard.active_subscribers_total === 1 ? '' : 's'}`} />
                <StatCard
                  title="Revenue (30d)"
                  value={`$${(paymentsDashboard.month_revenue_cents / 100).toFixed(2)}`}
                  sub={`${paymentsDashboard.month_payments} payments`}
                />
                <StatCard
                  title="Prev 30d"
                  value={`$${(paymentsDashboard.prev_month_revenue_cents / 100).toFixed(2)}`}
                  sub={paymentsDashboard.month_over_month_pct == null ? '—' :
                       paymentsDashboard.month_over_month_pct >= 0
                         ? `↑ ${paymentsDashboard.month_over_month_pct}% MoM`
                         : `↓ ${Math.abs(paymentsDashboard.month_over_month_pct)}% MoM`}
                />
                <StatCard
                  title="Active Subs"
                  value={paymentsDashboard.active_subscribers_total}
                  sub={paymentsDashboard.by_tier.map(t => `${t.role}: ${t.active_count}`).join(' · ') || 'none yet'}
                />
                <StatCard
                  title="Failed (30d)"
                  value={paymentsDashboard.failed_30d}
                  sub={paymentsDashboard.failed_30d > 0 ? 'check failed list below' : 'all green'}
                />
              </div>
            )}

            {/* MRR by tier breakdown */}
            {paymentsDashboard?.by_tier?.length > 0 && (
              <Card title="MRR by Tier">
                <div className="space-y-2">
                  {paymentsDashboard.by_tier.map(t => {
                    const totalMrr = paymentsDashboard.mrr_cents || 1;
                    const pct = (t.mrr_cents / totalMrr) * 100;
                    return (
                      <div key={t.role}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize"><RoleBadge role={t.role} /> {t.active_count} subscribers</span>
                          <span className="font-bold">${(t.mrr_cents / 100).toFixed(2)}/mo</span>
                        </div>
                        <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            style={{ width: pct + '%' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Revenue by type (subscription vs credit packs) */}
            {paymentsDashboard?.by_type?.length > 0 && (
              <Card title="Revenue split (30d)">
                <table className="w-full text-sm">
                  <thead className="text-left text-white/40 text-xs uppercase border-b border-slate-700">
                    <tr><th className="py-2">Type</th><th className="text-right">Payments</th><th className="text-right">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {paymentsDashboard.by_type.map(t => (
                      <tr key={t.type} className="border-b border-slate-800">
                        <td className="py-2 font-mono text-xs">{t.type || '(untyped)'}</td>
                        <td className="text-right text-xs">{t.count}</td>
                        <td className="text-right">${(t.total_cents / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {/* Top paying customers */}
            {paymentsDashboard?.top_customers?.length > 0 && (
              <Card title="Top Paying Customers (lifetime)">
                <table className="w-full text-sm">
                  <thead className="text-left text-white/40 text-xs uppercase border-b border-slate-700">
                    <tr><th className="py-2">Email</th><th>Role</th><th className="text-right">Payments</th><th className="text-right">Lifetime</th></tr>
                  </thead>
                  <tbody>
                    {paymentsDashboard.top_customers.map(c => (
                      <tr key={c.user_id} className="border-b border-slate-800">
                        <td className="py-2">{c.email}</td>
                        <td><RoleBadge role={c.role} /></td>
                        <td className="text-right text-xs">{c.payment_count}</td>
                        <td className="text-right text-green-300">${(c.lifetime_cents / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {/* Detail payments table */}
            <Card title={`Recent Payments${paymentsUserFilter.email ? ` — ${paymentsUserFilter.email}` : ''}`}>
              {paymentsUserFilter.email && (
                <div className="mb-3">
                  <span className="inline-flex items-center gap-1 bg-green-600/30 border border-green-500 rounded px-2 py-1 text-xs">
                    user: {paymentsUserFilter.email}
                    <button onClick={clearPaymentsFilter} className="ml-1 text-white/60 hover:text-white">×</button>
                  </span>
                </div>
              )}
              {payments.length === 0 ? <p className="text-white/40 text-sm py-8 text-center">No payments yet. Once a user subscribes or buys credits via Stripe, events appear here.</p> : (
                <table className="w-full text-sm">
                  <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                    <tr><th className="py-2">When</th><th>Type</th><th>User</th><th>Description</th><th className="text-right">Amount</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id} className="border-b border-slate-800">
                        <td className="py-2 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="text-xs">{p.type}</td>
                        <td className="text-xs">{p.user_id?.slice(0,12) || '—'}</td>
                        <td className="text-xs text-white/70">{p.description || '—'}</td>
                        <td className="text-right text-sm">${(p.amount_cents / 100).toFixed(2)}</td>
                        <td><span className={`text-xs px-2 py-0.5 rounded ${p.status === 'succeeded' || p.status === 'paid' ? 'bg-green-700' : p.status === 'failed' ? 'bg-red-700' : 'bg-slate-700'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        )}
      </main>

      {detailUserId && (
        <UserDetailModal
          userId={detailUserId}
          token={token}
          onClose={() => setDetailUserId(null)}
          onUserChanged={reloadUsers}
        />
      )}
    </div>
  );
}

function StatCard({ title, value, sub }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
      <p className="text-white/40 text-xs uppercase tracking-wide mb-1">{title}</p>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function RoleBadge({ role }) {
  const colors = {
    free: 'bg-slate-700 text-slate-200',
    pro: 'bg-purple-600 text-white',
    enterprise: 'bg-amber-600 text-white',
    comp: 'bg-green-600 text-white',
    admin: 'bg-red-600 text-white',
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${colors[role] || colors.free}`}>{role}</span>;
}

function StatusPill({ code }) {
  const color = code >= 500 ? 'bg-red-700' : code >= 400 ? 'bg-amber-700' : code >= 200 ? 'bg-green-700' : 'bg-slate-700';
  return <span className={`px-1.5 py-0.5 rounded ${color}`}>{code}</span>;
}

function Centered({ children }) {
  return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-8 text-center"><div className="text-white/60">{children}</div></div>;
}

// Human-friendly relative date (e.g. "2h ago", "3d ago", "Apr 12")
function relativeDate(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  if (days < 30) return Math.floor(days / 7) + 'w ago';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default AdminDashboard;
